// Registro del service worker que hace la web instalable como PWA.
//
// No se toca nada en native (Capacitor sirve los assets desde el propio
// dispositivo, ahí el SW no pinta nada) ni en desarrollo (un SW en localhost
// enmascara cambios y complica el hot reload).
import { isNative } from './native';

// ¿Se está ejecutando como app instalada (no como pestaña del navegador)?
// iOS no soporta display-mode: standalone en Safari antiguo y expone en su
// lugar navigator.standalone, por eso se comprueban las dos.
export function isStandalone() {
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

// Marca de "este dispositivo ya ha tenido sesión alguna vez", para no volver a
// enseñar la landing de captación a alguien que ya es usuario.
//
// Se guarda aparte del token a propósito: tokenManager.clearAll() borra una
// lista fija de claves al cerrar sesión, y esta debe SOBREVIVIR al logout.
// No es un identificador ni sirve para seguimiento: solo dice qué portal usar.
const PORTAL_KEY = 'mc_last_portal';

export function markReturningUser(portal) {
  try {
    localStorage.setItem(PORTAL_KEY, portal === 'parent' ? 'parent' : 'coach');
  } catch {
    /* modo privado o almacenamiento lleno: se pierde la marca, no pasa nada */
  }
}

export function getLastPortal() {
  try {
    return localStorage.getItem(PORTAL_KEY);
  } catch {
    return null;
  }
}

export function registerPWA() {
  if (isNative()) return;
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((e) => {
      console.info('[pwa] Service worker no registrado:', e?.message || e);
    });
  });
}

// ─── Instalación ─────────────────────────────────────────────────────────────
//
// Dos caminos, porque los navegadores no se ponen de acuerdo:
//
//  · Chrome / Edge / Samsung (Android y escritorio) disparan
//    'beforeinstallprompt'. Se guarda el evento y se lanza cuando el usuario
//    pulsa "Instalar", así el diálogo nativo sale dentro de un gesto suyo.
//  · Safari en iOS/iPadOS no implementa nada de esto: solo se puede explicar
//    el camino manual (Compartir → Añadir a pantalla de inicio).

let deferredPrompt = null;
const installListeners = new Set();

function notifyInstallListeners() {
  installListeners.forEach((fn) => {
    try { fn(); } catch { /* un suscriptor roto no debe tumbar a los demás */ }
  });
}

// Se llama al arrancar, no desde React: el evento puede llegar antes de que
// monte el primer componente y si no hay nadie escuchando se pierde.
export function initInstallPrompt() {
  if (isNative()) return;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Sin preventDefault, Chrome enseña su propia mini-barra y nos pisa el sitio.
    e.preventDefault();
    deferredPrompt = e;
    notifyInstallListeners();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    dismissInstallHint(true);
    notifyInstallListeners();
  });
}

export function canPromptInstall() {
  return deferredPrompt !== null;
}

export function subscribeInstall(fn) {
  installListeners.add(fn);
  return () => installListeners.delete(fn);
}

// Devuelve 'accepted' | 'dismissed' | 'unavailable'.
export async function promptInstall() {
  const evt = deferredPrompt;
  if (!evt) return 'unavailable';

  // El evento es de un solo uso: el navegador lo vuelve a emitir si procede.
  deferredPrompt = null;
  notifyInstallListeners();

  try {
    evt.prompt();
    const { outcome } = await evt.userChoice;
    if (outcome === 'accepted') dismissInstallHint(true);
    return outcome;
  } catch {
    return 'unavailable';
  }
}

// ¿Es un iPhone/iPad? iPadOS 13+ se anuncia como Macintosh, se distingue por
// tener puntos táctiles.
export function isIOSDevice() {
  try {
    const ua = navigator.userAgent || '';
    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1)
    );
  } catch {
    return false;
  }
}

// En iOS todos los navegadores son WebKit por dentro, pero solo Safari tiene
// "Añadir a pantalla de inicio" en su menú de compartir con el mismo recorrido.
export function isIOSSafari() {
  if (!isIOSDevice()) return false;
  const ua = navigator.userAgent || '';
  return !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/.test(ua);
}

// Qué se le puede ofrecer al usuario en este dispositivo:
//   'prompt'  → diálogo nativo del navegador (un botón y listo)
//   'ios'     → tutorial manual
//   'none'    → ya está instalada, es la app nativa o el navegador no puede
export function getInstallMode() {
  if (isNative() || isStandalone()) return 'none';
  if (canPromptInstall()) return 'prompt';
  if (isIOSDevice()) return 'ios';
  return 'none';
}

// Recordatorio pospuesto. Se guarda el momento a partir del cual se puede
// volver a enseñar el aviso; 'never' cuando ya se instaló o el usuario dijo
// que no quiere saber más del tema.
const INSTALL_HINT_KEY = 'mc_install_hint';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // 30 días

export function dismissInstallHint(forever = false) {
  try {
    localStorage.setItem(
      INSTALL_HINT_KEY,
      forever ? 'never' : String(Date.now() + SNOOZE_MS),
    );
  } catch {
    /* modo privado: como mucho el aviso reaparece en la siguiente visita */
  }
}

export function installHintSnoozed() {
  try {
    const v = localStorage.getItem(INSTALL_HINT_KEY);
    if (!v) return false;
    if (v === 'never') return true;
    return Date.now() < Number(v);
  } catch {
    return false;
  }
}
